/**
 * useDocumentSelection - Document Selection Handlers Hook
 * 
 * Extracts document selection logic from WorkspacePage.tsx:
 * - @open command document selection
 * - @edit command document selection (already open documents)
 * - @close command document selection
 * - Close all documents handler
 */

import { useCallback } from 'react';
import { logger } from '../utils/logger';
import type { AeroDocument } from '../types/document-schema';

interface OpenedDocumentForSelection {
  tabId: number;
  id?: string;
  documentNumber?: string;
  projectId?: string;
  title: string;
  jsonDocument?: AeroDocument;
  version?: string;
}

interface DocumentToOpen {
  id: string;
  documentNumber?: string;
  projectId?: string;
  title?: string;
  type?: 'project' | 'company' | 'standard' | 'prior-project';
  canEdit?: boolean;
  jsonDocument?: AeroDocument;
  version?: string;
}

interface DocumentSelectResult {
  id?: string;
  documentNumber?: string;
  projectId?: string;
  title?: string;
  type?: 'project' | 'company' | 'standard' | 'prior-project';
  canEdit?: boolean;
}

interface UseDocumentSelectionDeps {
  // From AIContext
  openedDocuments: OpenedDocumentForSelection[];
  addOpenedDocument: (doc: DocumentToOpen) => void;
  setActiveTabId: (id: number | null) => void;
  
  // Tab management
  handleCloseTab: (tabId: number) => void;
  handleCloseAll: () => void;
  
  // Edit mode
  handleEnableEdit: (tabId: number, jsonDocument: AeroDocument, version: string) => void;
  
  // Input control
  setInput: (value: string) => void;
  closeAllAutocomplete: () => void;
}

interface UseDocumentSelectionReturn {
  handleDocumentSelect: (result: DocumentSelectResult) => void;
  handleEditDocumentSelect: (tabId: number) => void;
  handleCloseDocumentSelect: (tabId: number) => void;
  handleCloseAllFromAutocomplete: () => void;
}

export function useDocumentSelection(deps: UseDocumentSelectionDeps): UseDocumentSelectionReturn {
  const {
    openedDocuments,
    addOpenedDocument,
    setActiveTabId,
    handleCloseTab,
    handleCloseAll,
    handleEnableEdit,
    setInput,
    closeAllAutocomplete,
  } = deps;

  /**
   * Handle document selection for @open command
   */
  const handleDocumentSelect = useCallback((result: DocumentSelectResult) => {
    // Save to recent documents (include status/revision for @edit command)
    try {
      const saved = localStorage.getItem('aeroRecentDocuments');
      const recent = saved ? JSON.parse(saved) : [];
      // Remove if already exists, add to front
      const filtered = recent.filter((r: { id?: string }) => r.id !== result.id);
      const docToSave = {
        ...result,
        status: result.status,
        currentRevision: result.currentRevision,
      };
      const updated = [docToSave, ...filtered].slice(0, 10); // Keep last 10
      localStorage.setItem('aeroRecentDocuments', JSON.stringify(updated));
    } catch (error) {
      logger.error('Failed to save recent document:', error);
    }
    
    // Add document to opened documents
    addOpenedDocument({
      id: result.id,
      documentNumber: result.documentNumber,
      projectId: result.projectId,
      title: result.title,
      type: result.type,
      canEdit: result.canEdit || false,
      status: result.status,
      currentRevision: result.currentRevision,
    });
    
    // Set as active tab
    setTimeout(() => {
      const doc = openedDocuments.find(d => d.id === result.id);
      if (doc) {
        setActiveTabId(doc.tabId);
      } else {
        // Just opened, will be the last one
        const maxTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map(d => d.tabId)) : 0;
        setActiveTabId(maxTabId + 1);
      }
    }, 100);
    
    // Clear input and autocomplete
    setInput('');
    closeAllAutocomplete();
  }, [openedDocuments, addOpenedDocument, setActiveTabId, setInput, closeAllAutocomplete]);

  /**
   * Handle document selection for @edit command (already open document)
   */
  const handleEditDocumentSelect = useCallback((tabId: number) => {
    logger.debug('[useDocumentSelection] @edit: Selected tab', tabId);
    
    const doc = openedDocuments.find(d => d.tabId === tabId);
    if (!doc) {
      logger.warn('[useDocumentSelection] @edit: Tab not found:', tabId);
      return;
    }

    // Switch to this tab
    setActiveTabId(tabId);
    
    // Enable edit mode
    if (doc.jsonDocument) {
      logger.debug('[useDocumentSelection] @edit: Document loaded, enabling edit directly');
      handleEnableEdit(tabId, doc.jsonDocument, doc.version || '1');
    } else {
      // Wait for document to load, then try to enable edit
      logger.debug('[useDocumentSelection] @edit: Waiting for document to load...');
      setTimeout(() => {
        const updatedDoc = openedDocuments.find(d => d.tabId === tabId);
        if (updatedDoc?.jsonDocument) {
          handleEnableEdit(tabId, updatedDoc.jsonDocument, updatedDoc.version || '1');
        } else {
          // Fallback: try clicking the button
          const enableButton = document.querySelector('[data-edit-button]') as HTMLButtonElement;
          if (enableButton) {
            enableButton.click();
            logger.debug('[useDocumentSelection] @edit: Clicked Enable Editing button');
          }
        }
      }, 500);
    }
    
    // Clear input and autocomplete
    setInput('');
    closeAllAutocomplete();
  }, [openedDocuments, setActiveTabId, handleEnableEdit, setInput, closeAllAutocomplete]);

  /**
   * Handle tab selection for @close command
   */
  const handleCloseDocumentSelect = useCallback((tabId: number) => {
    logger.debug('[useDocumentSelection] @close: Selected tab', tabId);
    handleCloseTab(tabId);
    
    // Clear input and autocomplete
    setInput('');
    closeAllAutocomplete();
  }, [handleCloseTab, setInput, closeAllAutocomplete]);

  /**
   * Handle close all from @close command
   */
  const handleCloseAllFromAutocomplete = useCallback(() => {
    logger.debug('[useDocumentSelection] @close all: Closing all tabs');
    handleCloseAll();
    
    // Clear input and autocomplete
    setInput('');
    closeAllAutocomplete();
  }, [handleCloseAll, setInput, closeAllAutocomplete]);

  return {
    handleDocumentSelect,
    handleEditDocumentSelect,
    handleCloseDocumentSelect,
    handleCloseAllFromAutocomplete,
  };
}
